/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import analytics from '../../utils/analytics';
import { 
  Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter 
} from "../ui/card";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogFooter, DialogDescription 
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from '../ui/table';
import { Badge } from "../ui/badge";
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Separator } from "../ui/separator";
import { 
  Select, SelectContent, SelectItem, 
  SelectTrigger, SelectValue 
} from "../ui/select";
import { 
  Loader2, AlertTriangle, CheckCircle2, Info,
  PlusCircle, Edit, Trash, Eye,
  Users, RefreshCcw, RotateCw, BookCopy, ChevronUp, ChevronDown, Share2
} from "lucide-react";
import ImageSelector from './ImageSelector';
import logger from '../../utils/logger';

const LOCAL_STORAGE_KEY = 'testManagerState';

const TestManager = ({ initialShowInactive = false, teacherContext = false, teacherId = null }) => {
  // auth state - must be declared early as it's used in computed values
  const [authState, setAuthState] = useState(null);
  
  // Custom toast state
  const [toasts, setToasts] = useState([]);
  
  // Custom toast functions
  const toast = useCallback(({ title, description, variant = 'default' }) => {
    const id = Date.now().toString();
    const newToast = {
      id,
      title,
      description,
      variant
    };
    
    setToasts(prev => [...prev, newToast]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  }, []);
  
  // API endpoint helper that respects context
  const getApiEndpoint = (resource, id = null, action = null) => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const context = teacherContext ? 'teacher' : 'admin';
    
    if (id && action) {
      return `${baseUrl}/api/${context}/${resource}/${id}/${action}`;
    } else if (id) {
      return `${baseUrl}/api/${context}/${resource}/${id}`;
    } else {
      return `${baseUrl}/api/${context}/${resource}`;
    }
  };

  // Unified ownership checking with proper type handling
  const currentUserId = teacherContext ? 
    (typeof teacherId === 'string' ? parseInt(teacherId, 10) : teacherId) : 
    (typeof authState?.userId === 'string' ? parseInt(authState.userId, 10) : authState?.userId);
  
  // Get all available categories (built-in + custom)
  const getAllCategories = () => {
    const builtInCategories = ['woodshop', 'auto', 'metal', 'general'];
    return [...builtInCategories, ...customCategories];
  };
  
  // Add a new custom category
  const handleAddCategory = () => {
    if (newCategoryName.trim() && !getAllCategories().includes(newCategoryName.trim().toLowerCase())) {
      setCustomCategories([...customCategories, newCategoryName.trim().toLowerCase()]);
      setNewCategoryName('');
      setIsAddCategoryDialogOpen(false);
      
      toast({
        title: "Category Added",
        description: `"${newCategoryName.trim()}" has been added as a custom category.`,
      });
    }
  };
  
  // Get question type options
  const getQuestionTypes = () => [
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'true_false', label: 'True/False' },
    { value: 'written', label: 'Written Response' }
  ];
  
  // Handle dynamic answer changes for multiple choice questions
  const handleDynamicAnswerChange = (index, value) => {
    const newAnswers = [...dynamicAnswers];
    newAnswers[index] = value;
    
    // Add a new empty field if user is typing in the last field and it's not empty
    if (index === newAnswers.length - 1 && value.trim() !== '') {
      newAnswers.push('');
    }
    
    // Remove empty fields from the end (except always keep at least 2)
    while (newAnswers.length > 2 && newAnswers[newAnswers.length - 1] === '' && newAnswers[newAnswers.length - 2] === '') {
      newAnswers.pop();
    }
    
    setDynamicAnswers(newAnswers);
    
    // Update the answers for the form submission
    const filteredAnswers = newAnswers.filter(answer => answer.trim() !== '');
    setAnswers(filteredAnswers);
    setNewQuestion({...newQuestion, answers: filteredAnswers.join('\n')});
  };
  
  // Reset dynamic answers when question type changes
  const resetDynamicAnswers = () => {
    setDynamicAnswers(['', '']);
    setAnswers([]);
  };

  // Handle moving test up in order
  const handleMoveTestUp = (index) => {
    if (index === 0) return;
    const newOrderedTests = [...orderedTests];
    [newOrderedTests[index - 1], newOrderedTests[index]] = [newOrderedTests[index], newOrderedTests[index - 1]];
    setOrderedTests(newOrderedTests);
  };
  
  // Handle moving test down in order
  const handleMoveTestDown = (index) => {
    if (index === orderedTests.length - 1) return;
    const newOrderedTests = [...orderedTests];
    [newOrderedTests[index], newOrderedTests[index + 1]] = [newOrderedTests[index + 1], newOrderedTests[index]];
    setOrderedTests(newOrderedTests);
  };
  
  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };
  
  const handleDragEnd = (e) => {
    // Reset visual feedback
    e.target.style.opacity = '1';
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (dragIndex === dropIndex) return;
    
    const newOrderedTests = [...orderedTests];
    const draggedTest = newOrderedTests[dragIndex];
    
    // Remove the dragged item
    newOrderedTests.splice(dragIndex, 1);
    // Insert it at the new position
    newOrderedTests.splice(dropIndex, 0, draggedTest);
    
    setOrderedTests(newOrderedTests);
  };
  
  // Save the new test order
  const handleSaveTestOrder = async () => {
    setIsReordering(true);
    try {
      // Update display_order for each test based on new position
      const updates = orderedTests.map((test, index) => ({
        id: test.id,
        display_order: index + 1
      }));
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tests/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': authState?.sessionId
        },
        credentials: 'include',
        body: JSON.stringify({ updates })
      });
      
      if (!response.ok) throw new Error('Failed to reorder tests');
      
      // Refresh the tests list
      await fetchTests();
      
      toast({
        title: "Success",
        description: "Test order updated successfully",
      });
    } catch (error) {
      handleError(error, 'Failed to save test order');
      // Reset to original order on error
      const activeTests = tests
        .filter(test => !test.deleted_at)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setOrderedTests(activeTests);
    } finally {
      setIsReordering(false);
    }
  };
  
  const canEditTest = (test) => {
    // Ensure proper type comparison for user IDs
    const testCreatorId = typeof test.creator_id === 'string' ? parseInt(test.creator_id, 10) : test.creator_id;
    const userId = typeof currentUserId === 'string' ? parseInt(currentUserId, 10) : currentUserId;

    // Enhanced debugging for production issues
    const result = !teacherContext || testCreatorId === userId;

    logger.info('can_edit_test_check', {
      testId: test.id,
      testName: test.name,
      testCreatorId: test.creator_id,
      testCreatorIdNormalized: testCreatorId,
      currentUserId,
      currentUserIdNormalized: userId,
      teacherContext,
      teacherId,
      authStateUserId: authState?.userId,
      result,
      typeComparison: `${testCreatorId} === ${userId}`,
      reason: !teacherContext ? 'admin_context' : (testCreatorId === userId ? 'own_test' : 'not_own_test'),
      timestamp: new Date().toISOString()
    });

    if (!teacherContext) return true; // Admin can edit everything
    return testCreatorId === userId; // Teacher can only edit own tests with proper type comparison
  };
  
  const canDeleteTest = (test) => {
    if (!teacherContext) return true; // Admin can delete everything
    return test.creator_id === currentUserId; // Teacher can only delete own tests
  };
  
  const canShareTest = (test) => {
    if (!teacherContext) return false; // Admin sharing not implemented
    return test.creator_id === currentUserId; // Can only share own tests
  };
  
  // Load state from localStorage if available
  const loadInitialState = () => {
    try {
      const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        // Check if the saved state is recent enough (within the last hour)
        const savedTime = new Date(parsedState.timestamp || 0);
        const currentTime = new Date();
        const hourInMs = 60 * 60 * 1000;
        
        if (currentTime - savedTime < hourInMs) {
          return parsedState.data;
        }
      }
    } catch (error) {
      logger.error('failed_to_load_state', {
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
    
    // Default state if nothing is loaded
    return {
      tests: [],
      selectedTest: null,
      isNewTest: false,
      testDetails: { 
        name: '', 
        description: '', 
        timeLimit: 60, 
        maxRetries: 0 
      },
      questions: [],
      editingQuestion: null,
      newQuestion: { 
        question_text: '', 
        answers: '', 
        correct_answer: '' 
      },
      isQuestionDialogOpen: false,
      answers: [],
      showInactiveTests: initialShowInactive,
      showInactiveQuestions: initialShowInactive,
      assignments: [],
      isAssignmentDialogOpen: false,
      assigningTest: null,
      editingTest: null
    };
  };


  // initialize state from localStorage or defaults
  const initialState = loadInitialState();

  // state declartions for tests and test details
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [isNewTest, setIsNewTest] = useState(false);
  const [testDetails, setTestDetails] = useState({ 
    name: '',
    description: '',
    timeLimit: 60,
    maxRetries: 0,
    retryCooldownMinutes: 5,
    displayOrder: tests.length + 1,
    category: 'woodshop',
    passThreshold: 90.00
  });

  // state declarations for questions
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    answers: '',
    correct_answer: '',
    question_type: 'multiple_choice',
    image_url: null
  });
  const [answers, setAnswers] = useState([]);
  const [dynamicAnswers, setDynamicAnswers] = useState(['', '']); // Start with 2 empty answer fields

  // state declarations for assignments
  const [assignments, setAssignments] = useState([]);
  const [assigningTest, setAssigningTest] = useState(null);
  const [cohorts, setCohorts] = useState([]);

  // state declarations for ui state
  const [activeTab, setActiveTab] = useState("tests");
  const [showInactiveTests] = useState(initialState.showInactiveTests);
  const [showInactiveQuestions, setShowInactiveQuestions] = useState(initialState.showInactiveQuestions);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecounting, setIsRecounting] = useState(false);
  const [, setLastRecountStatus] = useState(null);

  // Utility function to format friendly relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Just created';
    
    const now = new Date();
    const date = new Date(dateString);
    
    // Check if date is invalid
    if (isNaN(date.getTime())) return 'Just created';
    
    // Debug logging to check timestamps
    console.log('Formatting time for:', dateString);
    console.log('Parsed date:', date);
    console.log('Current time:', now);
    
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    console.log('Time difference in minutes:', diffMinutes);
    console.log('Time difference in hours:', diffHours);
    
    // Simplified and more accurate time formatting
    if (diffMinutes < 1) {
      return 'just now';
    } else if (diffMinutes < 60) {
      return `last edited ${diffMinutes}m ago - ${date.toLocaleDateString()}`;
    } else if (diffHours < 24) {
      const remainingMinutes = diffMinutes % 60;
      if (remainingMinutes === 0) {
        return `last edited ${diffHours}h ago - ${date.toLocaleDateString()}`;
      } else {
        return `last edited ${diffHours}h ${remainingMinutes}m ago - ${date.toLocaleDateString()}`;
      }
    } else if (diffDays < 7) {
      const remainingHours = diffHours % 24;
      if (remainingHours === 0) {
        return `last edited ${diffDays}d ago - ${date.toLocaleDateString()}`;
      } else {
        return `last edited ${diffDays}d ${remainingHours}h ago - ${date.toLocaleDateString()}`;
      }
    } else {
      return `last edited ${date.toLocaleDateString()}`;
    }
  };

  // Clean relative time utility for stats dialog
  const formatRelativeTimeClean = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const now = new Date();
    const date = new Date(timestamp);
    
    if (isNaN(date.getTime())) return 'Unknown';
    
    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);
    
    if (diffSeconds < 30) {
      return 'just now';
    } else if (diffSeconds < 60) {
      return `${diffSeconds}s ago`;
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else if (diffWeeks < 4) {
      return `${diffWeeks}w ago`;
    } else if (diffMonths < 12) {
      return `${diffMonths}mo ago`;
    } else {
      return `${diffYears}y ago`;
    }
  };

  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [customCategories, setCustomCategories] = useState([]);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [orderedTests, setOrderedTests] = useState([]);
  const [isReordering, setIsReordering] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter] = useState('all');
  
  // Enhanced filtering states
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('mine');
  const [showTrash, setShowTrash] = useState(false);
  
  // Sorting states
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // state declarations for dialogs
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [isQuestionDialogOpen, setIsQuestionDialogOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [sharingTest, setSharingTest] = useState(null);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [initialSharedTeachers, setInitialSharedTeachers] = useState([]);
  const [editingTest, setEditingTest] = useState(null);
  const [, setEditAction] = useState(null);
  
  // Test stats dialog states
  const [isStatsDialogOpen, setIsStatsDialogOpen] = useState(false);
  const [statsTest, setStatsTest] = useState(null);
  const [testStats, setTestStats] = useState(null);

  // Delete question confirmation dialog states
  const [isDeleteQuestionDialogOpen, setIsDeleteQuestionDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);


// Initialize ordered tests when tests change
useEffect(() => {
  if (tests.length > 0) {
    const activeTests = tests
      .filter(test => !test.deleted_at)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setOrderedTests(activeTests);
  }
}, [tests]);

// === Authentication Check ===
useEffect(() => {
  const checkAuth = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/check`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Authentication check failed');
      }
      
      const data = await response.json();
      setAuthState(data);
      
      logger.info('auth_check_success', {
        isAdmin: data.isAdmin,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      logger.error('auth_check_failed', {
        error: error.message,
        timestamp: new Date().toISOString()
      });
      
      // Redirect to login if auth fails
      window.location.href = '/login';
    }
  };

  checkAuth();
}, []);


  useEffect(() => {
    const stateToSave = {
      tests,
      selectedTest,
      isNewTest,
      testDetails,
      questions,
      editingQuestion,
      newQuestion,
      isQuestionDialogOpen,
      answers,
      showInactiveTests,
      showInactiveQuestions,
      assignments,
      isAssignmentDialogOpen,
      assigningTest,
      editingTest
    };

    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
        data: stateToSave,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
      logger.error('failed_to_save_state', {
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  }, [
    tests, selectedTest, isNewTest, testDetails, questions,
    editingQuestion, newQuestion, isQuestionDialogOpen,
    answers, showInactiveTests, showInactiveQuestions, assignments,
    isAssignmentDialogOpen, assigningTest, editingTest
  ]);

  // capture browser beforeunload event to warn user about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (editingTest || editingQuestion || isNewTest || isQuestionDialogOpen || isAssignmentDialogOpen) {
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [editingTest, editingQuestion, isNewTest, isQuestionDialogOpen, isAssignmentDialogOpen]);


  // === Data Fetching Functions ===
  const fetchTests = useCallback(async () => {
    try {
      setIsLoading(true);
      logger.info('fetching_tests', {
        showInactive: showInactiveTests,
        timestamp: new Date().toISOString()
      });

      const params = new URLSearchParams();
      
      // Include deleted tests based on checkboxes and filters (trash-only system)
      if (showInactiveTests || showTrash || statusFilter === 'deleted') {
        params.append('includeDeleted', 'true');
      }
      
      // Pass teacher context for proper permission filtering
      if (teacherContext) {
        params.append('teacherContext', 'true');
      }
      
      const queryString = params.toString();
      const endpoint = getApiEndpoint('tests');
      const response = await fetch(
        `${endpoint}${queryString ? `?${queryString}` : ''}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Id': authState?.sessionId
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch tests');
      
      const data = await response.json();
      logger.info('tests_fetched', {
        testCount: data.length,
        timestamp: new Date().toISOString()
      });
      
      setTests(data);
    } catch (error) {
      logger.error('tests_fetch_failed', {
        error: error.message,
        timestamp: new Date().toISOString()
      });
      toast({
        title: 'Error',
        description: 'Failed to fetch tests',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [showInactiveTests, showTrash, statusFilter, teacherContext, toast]);

  // Use a ref to track if focus/visibility refresh should be enabled
  const shouldRefreshOnFocus = useRef(true);
  
  // === Force Fresh State on Window Focus ===
  useEffect(() => {
    const handleFocus = () => {
      if (shouldRefreshOnFocus.current && authState) {
        logger.info('window_focus_refresh_triggered');
        // Clear localStorage cache to force fresh data
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        // Trigger refresh by updating a dependency
        setIsLoading(true);
        fetchTests();
      }
    };

    const handleVisibilityChange = () => {
      if (shouldRefreshOnFocus.current && !document.hidden && authState) {
        logger.info('visibility_change_refresh_triggered');
        // Clear cache and refresh when tab becomes visible
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        setIsLoading(true);
        fetchTests();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authState, fetchTests]);

  const fetchTeachers = useCallback(async () => {
    try {
      // Always use admin endpoint for fetching teachers list for sharing
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/teachers`, {
        credentials: 'include',
        headers: {
          'X-Session-Id': authState?.sessionId
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch teachers');
      
      const teachers = await response.json();
      setAvailableTeachers(teachers);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast({
        title: "Error",
        description: "Failed to fetch teachers for sharing",
        variant: 'destructive'
      });
    }
  }, [authState?.sessionId, toast]);

  const fetchCohorts = useCallback(async () => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/api/cohorts`;
      let data;
      
      if (teacherContext && teacherId) {
        // For teachers, get their assigned cohorts directly
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/teacher/cohorts`, {
          credentials: 'include',
          headers: {
            'X-Session-Id': authState?.sessionId
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch teacher cohorts');
        
        data = await response.json();
        
      } else {
        // For admins, get all cohorts
        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            'X-Session-Id': authState?.sessionId
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch cohorts');
        data = await response.json();
      }
      
      setCohorts(data);
      
      logger.info('cohorts_fetched', {
        count: data.length,
        teacherContext,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      logger.error('cohorts_fetch_failed', {
        error: error.message,
        teacherContext,
        timestamp: new Date().toISOString()
      });
      toast({
        title: 'Error',
        description: 'Failed to load cohorts',
        variant: 'destructive'
      });
    }
  }, [authState?.sessionId, teacherContext, teacherId, toast]);

  const fetchAssignments = useCallback(async () => {
    try {
      // Use different endpoints for teachers vs admins
      const endpoint = teacherContext 
        ? `${process.env.REACT_APP_API_URL}/api/teacher/tests/assignments`
        : `${process.env.REACT_APP_API_URL}/api/tests/cohort-assignments`;
      
      const response = await fetch(endpoint, {
        credentials: 'include',
        headers: {
          'X-Session-Id': authState?.sessionId
        }
      });

      if (!response.ok) throw new Error('Failed to fetch test assignments');
      
      const data = await response.json();
      setAssignments(data);
      
      logger.info('assignments_fetched', {
        count: data.length,
        teacherContext,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      logger.error('assignments_fetch_failed', {
        error: error.message,
        teacherContext,
        timestamp: new Date().toISOString()
      });
      toast({
        title: 'Error',
        description: 'Failed to load test assignments',
        variant: 'destructive'
      });
    }
  }, [authState?.sessionId, teacherContext, toast]);

  const fetchQuestions = useCallback(async (testId) => {
    if (!testId) return;

    try {
      setIsLoading(true);

      // Clear existing questions to ensure clean state
      setQuestions([]);

      logger.info('fetching_questions', {
        testId,
        showInactive: showInactiveQuestions,
        timestamp: new Date().toISOString()
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/tests/${testId}/questions${showInactiveQuestions ? '?includeInactive=true' : ''}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Id': authState?.sessionId
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch questions');
      
      const data = await response.json();
      logger.info('questions_fetched', {
        testId,
        questionCount: data.length,
        timestamp: new Date().toISOString()
      });
      
      // Process the answers for each question
      const processedQuestions = data.map(question => ({
        ...question,
        answers: Array.isArray(question.answers) 
          ? question.answers 
          : JSON.parse(question.answers || '[]')
      }));
      
      // Force state update with completely fresh data
      setQuestions(processedQuestions);
    } catch (error) {
      logger.error('questions_fetch_failed', {
        error: error.message,
        testId,
        timestamp: new Date().toISOString()
      });
      toast({
        title: 'Error',
        description: 'Failed to fetch questions',
        variant: 'destructive'
      });
      // Ensure questions is cleared on error too
      setQuestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [showInactiveQuestions, toast, authState?.sessionId]);

  // === Initialization Effects ===
  
  useEffect(() => {
    if (authState) {
      fetchTests();
    }
  }, [authState, showInactiveTests, showTrash, statusFilter, teacherContext]);

  useEffect(() => {
    if (authState) {
      fetchCohorts();
      fetchAssignments();
    }
  }, [authState, teacherContext, teacherId]);


  useEffect(() => {
    if (selectedTest && !isNewTest) {
      fetchQuestions(selectedTest.id);
      setTestDetails(prev => ({
        name: selectedTest.name,
        description: selectedTest.description,
        timeLimit: selectedTest.time_limit || 60,
        maxRetries: selectedTest.max_retries || 0,
        retryCooldownMinutes: selectedTest.retry_cooldown_minutes ?? 5,
        displayOrder: selectedTest.display_order || 0,
        category: selectedTest.category || prev.category || 'woodshop', // Preserve user's category selection
        passThreshold: selectedTest.pass_threshold || 90.00
      }));
    }
  }, [selectedTest, isNewTest, fetchQuestions]);

  useEffect(() => {
    if (newQuestion.answers) {
      const answerList = newQuestion.answers.split('\n').filter(answer => answer.trim() !== '');
      setAnswers(answerList);
    }
  }, [newQuestion.answers]);

  useEffect(() => {
    if (editingTest) {
      setTestDetails({
        name: editingTest.name,
        description: editingTest.description,
        timeLimit: editingTest.time_limit || 60,
        maxRetries: editingTest.max_retries || 0,
        retryCooldownMinutes: editingTest.retry_cooldown_minutes ?? 5,
        displayOrder: editingTest.display_order || 0,
        category: editingTest.category || 'woodshop',
        passThreshold: editingTest.pass_threshold || 90.00
      });
    }
  }, [editingTest]);

  // === Error Handling ===
  
  const handleError = (error, defaultMessage) => {
    logger.error('operation_failed', {
      error: error.message,
      timestamp: new Date().toISOString()
    });

    if (error.message === 'Unauthorized' || error.message === 'Authentication failed') {
      toast({
        title: 'Authentication Error',
        description: 'Please log in again',
        variant: 'destructive'
      });
      window.location.href = '/login';
      return;
    }

    toast({
      title: 'Error',
      description: error.message || defaultMessage,
      variant: 'destructive'
    });
  };

  // === Action Handlers ===
  
  const handleTestSelect = (testId) => {
    if (testId === 'new') {
      setIsNewTest(true);
      setSelectedTest(null);
      setTestDetails(prev => ({
        name: '',
        description: '',
        timeLimit: 60,
        maxRetries: 0,
        displayOrder: tests.length + 1,
        category: prev.category || 'woodshop', // Preserve current category selection
        passThreshold: 90.00
      }));
      setQuestions([]);
      setActiveTab("details");
    } else {
      setIsNewTest(false);
      const test = tests.find(test => test.id.toString() === testId);
      setSelectedTest(test);
      setActiveTab("details");
    }
  };

  // Handle test editing with immediate copy-on-edit for non-owned tests
  const handleTestEdit = async (test) => {
    logger.info('test_edit_initiated', {
      testId: test.id,
      testName: test.name,
      testCreatorId: test.creator_id,
      currentUserId,
      teacherContext,
      canEdit: canEditTest(test),
      timestamp: new Date().toISOString()
    });

    // Check if user can directly edit this test
    if (canEditTest(test)) {
      // Direct edit - user owns the test or is admin
      logger.info('test_direct_edit', {
        testId: test.id,
        reason: 'User can edit this test directly',
        teacherContext,
        isOwnTest: parseInt(test.creator_id) === parseInt(currentUserId)
      });

      setEditingTest(test);
      setSelectedTest(test);
      setEditAction("edit");
      setActiveTab("details");

      // Load test details for editing
      setTestDetails({
        name: test.name,
        description: test.description,
        timeLimit: test.time_limit,
        maxRetries: test.max_retries,
        retryCooldownMinutes: test.retry_cooldown_minutes,
        displayOrder: test.display_order,
        category: test.category,
        passThreshold: test.pass_threshold
      });

      // Load questions for the test
      fetchQuestions(test.id);
    } else {
      // Copy-on-edit required - user doesn't own the test
      logger.info('test_copy_on_edit_required', {
        testId: test.id,
        reason: 'User cannot edit this test directly - creating copy',
        teacherContext,
        isOwnTest: parseInt(test.creator_id) === parseInt(currentUserId)
      });

      try {
        setIsLoading(true);

        // Show initial toast
        toast({
          title: "Creating Your Copy",
          description: `Creating your copy of "${test.name}"...`,
          variant: "default"
        });

        // Use the copy-and-edit route to create a copy immediately
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tests/${test.id}/copy-and-edit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Id': authState?.sessionId
          },
          credentials: 'include',
          body: JSON.stringify({
            name: test.name,
            description: test.description,
            time_limit: test.time_limit,
            max_retries: test.max_retries,
            retry_cooldown_minutes: test.retry_cooldown_minutes,
            display_order: test.display_order,
            category: test.category,
            pass_threshold: test.pass_threshold
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create copy');
        }

        const copiedTestData = await response.json();

        logger.info('test_copy_created', {
          originalTestId: test.id,
          newTestId: copiedTestData.id,
          newTestName: copiedTestData.name
        });

        // Force fresh data and refresh the tests list to show the new copy
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        await fetchTests();

        // Find the newly created test copy in the refreshed list
        const clonedTest = tests.find(t => t.id === copiedTestData.id);
        if (clonedTest) {
          logger.info('cloned_test_found_immediately', {
            newTestId: copiedTestData.id,
            testFound: true
          });

          // Immediately switch to editing the new copy
          setSelectedTest(clonedTest);
          setEditingTest(clonedTest);
          setEditAction("edit");
          setActiveTab("edit");

          // Load test details for the copy
          setTestDetails({
            name: clonedTest.name,
            description: clonedTest.description,
            timeLimit: clonedTest.time_limit,
            maxRetries: clonedTest.max_retries,
            retryCooldownMinutes: clonedTest.retry_cooldown_minutes,
            displayOrder: clonedTest.display_order,
            category: clonedTest.category,
            passThreshold: clonedTest.pass_threshold
          });

          // Load questions for the copied test
          await fetchQuestions(clonedTest.id);

          // Show success toast with the new test name
          toast({
            title: "Copy Created!",
            description: `Now editing "${clonedTest.name}". This is your own copy to modify.`,
            duration: 5000,
          });
        } else {
          logger.warn('cloned_test_not_found_immediately', {
            newTestId: copiedTestData.id,
            availableTestIds: tests.map(t => t.id),
            retryingWithDelay: true
          });

          // Fallback: try to find the test after a brief delay with multiple attempts
          const findTestWithRetries = async (attempts = 3) => {
            for (let i = 0; i < attempts; i++) {
              await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))); // Increasing delay
              await fetchTests();

              // Look for the test in the refreshed tests list instead of trying to fetch individually
              const delayedTest = tests.find(t => t.id === copiedTestData.id);

              if (delayedTest) {
                logger.info('cloned_test_found_after_retry', {
                  newTestId: copiedTestData.id,
                  attemptNumber: i + 1,
                  testFound: true,
                  hasCreatorId: !!delayedTest.creator_id
                });

                setSelectedTest(delayedTest);
                setEditingTest(delayedTest);
                setEditAction("edit");
                setActiveTab("edit");

                // Load test details for the copy
                setTestDetails({
                  name: delayedTest.name,
                  description: delayedTest.description,
                  timeLimit: delayedTest.time_limit,
                  maxRetries: delayedTest.max_retries,
                  retryCooldownMinutes: delayedTest.retry_cooldown_minutes,
                  displayOrder: delayedTest.display_order,
                  category: delayedTest.category,
                  passThreshold: delayedTest.pass_threshold
                });

                // Load questions for the copied test
                await fetchQuestions(delayedTest.id);

                toast({
                  title: "Copy Created!",
                  description: `Now editing "${delayedTest.name}". This is your own copy to modify.`,
                  duration: 5000,
                });
                return;
              }

              logger.warn('cloned_test_not_found_attempt', {
                newTestId: copiedTestData.id,
                attemptNumber: i + 1,
                availableTestIds: tests.map(t => t.id)
              });
            }

            // If we still can't find it, show a different message
            logger.error('cloned_test_never_found', {
              newTestId: copiedTestData.id,
              attemptsExhausted: attempts
            });

            toast({
              title: "Copy Created",
              description: `${copiedTestData.name} was created. Please refresh the page to see it in your test list.`,
              duration: 5000,
            });
          };

          findTestWithRetries();
        }

      } catch (error) {
        logger.error('test_copy_failed', {
          testId: test.id,
          error: error.message,
          stack: error.stack
        });

        toast({
          title: "Error",
          description: error.message || "Failed to create copy for editing",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleShareTest = async (test) => {
    setSharingTest(test);
    setSelectedTeachers([]);
    await fetchTeachers();
    
    // Fetch current sharing state (teacher-only functionality)
    if (teacherContext) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/teacher/tests/${test.id}/sharing`,
          {
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              'X-Session-Id': authState?.sessionId
            }
          }
        );
      
        if (response.ok) {
          const sharingData = await response.json();
          const currentlySharedIds = sharingData.sharedWith.map(s => s.teacher_id);
          setSelectedTeachers(currentlySharedIds);
          setInitialSharedTeachers(currentlySharedIds);
        }
      } catch (error) {
        logger.error('failed_to_fetch_sharing_state', {
          testId: test.id,
          error: error.message
        });
      }
    }
    
    setIsShareDialogOpen(true);
  };

  const handleShareSubmit = async () => {
    if (!sharingTest) return;

    // Calculate changes
    const teachersToShare = selectedTeachers.filter(id => !initialSharedTeachers.includes(id));
    const teachersToUnshare = initialSharedTeachers.filter(id => !selectedTeachers.includes(id));
    
    // If no changes, just close the dialog
    if (teachersToShare.length === 0 && teachersToUnshare.length === 0) {
      setIsShareDialogOpen(false);
      setSharingTest(null);
      setSelectedTeachers([]);
      setInitialSharedTeachers([]);
      return;
    }

    setIsLoading(true);
    
    try {
      // Sharing is currently teacher-only functionality
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/teacher/tests/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': authState?.sessionId
        },
        credentials: 'include',
        body: JSON.stringify({
          testId: sharingTest.id,
          teachersToShare: teachersToShare.length > 0 ? teachersToShare : undefined,
          teachersToUnshare: teachersToUnshare.length > 0 ? teachersToUnshare : undefined,
          accessLevel: 'read' // Default to read access
        })
      });

      if (!response.ok) throw new Error('Failed to update test sharing');

      const result = await response.json();

      toast({
        title: "Sharing Updated",
        description: result.message || "Test sharing updated successfully",
      });

      setIsShareDialogOpen(false);
      setSharingTest(null);
      setSelectedTeachers([]);
      setInitialSharedTeachers([]);
      
      // Force fresh data after sharing to prevent state corruption
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      await fetchTests();
    } catch (error) {
      handleError(error, 'Failed to update test sharing');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let url, method;

      if (isNewTest) {
        // Creating a new test
        url = `${process.env.REACT_APP_API_URL}/api/admin/tests`;
        method = 'POST';
      } else {
        // Determine if this is a direct edit or copy-on-edit based on ownership
        const canDirectEdit = canEditTest(selectedTest);

        // DEBUG: Log the decision process
        console.log('🔍 SAVE DECISION DEBUG:', {
          selectedTestId: selectedTest?.id,
          selectedTestName: selectedTest?.name,
          selectedTestCreatorId: selectedTest?.creator_id,
          currentUserId,
          teacherContext,
          canDirectEdit,
          newName: testDetails.name,
          nameChanged: selectedTest?.name !== testDetails.name
        });

        if (canDirectEdit) {
          // Direct edit - user owns the test
          console.log('✅ Using PUT (direct edit)');
          url = `${process.env.REACT_APP_API_URL}/api/admin/tests/${selectedTest.id}`;
          method = 'PUT';
        } else {
          // Copy-on-edit - user doesn't own the test
          console.log('🔄 Using POST (copy-on-edit)');
          url = `${process.env.REACT_APP_API_URL}/api/admin/tests/${selectedTest.id}/copy-and-edit`;
          method = 'POST';
        }
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': authState?.sessionId
        },
        credentials: 'include',
        body: JSON.stringify({
          name: testDetails.name,
          description: testDetails.description,
          time_limit: testDetails.timeLimit,
          max_retries: testDetails.maxRetries,
          retry_cooldown_minutes: testDetails.retryCooldownMinutes,
          display_order: testDetails.displayOrder,
          category: testDetails.category,
          pass_threshold: testDetails.passThreshold
        }),
      });
      
      if (!response.ok) throw new Error('Failed to save test');
      
      const data = await response.json();
      
      if (isNewTest) {
        setSelectedTest(data);
        setIsNewTest(false);
        
        // Track test creation
        analytics.trackTestCreation(
          data.id,
          testDetails.name,
          'admin', // Creator role
          0 // Initial question count
        );
      } else {
        // Handle copy-on-edit: detect if this was a copy-on-edit operation based on response format
        if (data.newTestId) {
          // This was a copy-on-edit operation - fetch the new test details
          console.log('Copy-on-edit detected: switching to new test copy', {
            originalTestId: selectedTest.id,
            newTestId: data.newTestId
          });

          try {
            // Fetch the complete test details for the new copy
            const newTestResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tests/${data.newTestId}`, {
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-Session-Id': authState?.sessionId
              }
            });

            if (newTestResponse.ok) {
              const newTestData = await newTestResponse.json();

              // Update selected test to the new copy
              setSelectedTest(newTestData);
              setEditingTest(newTestData);

              // Update test details to reflect the new test
              setTestDetails({
                name: newTestData.name,
                description: newTestData.description,
                timeLimit: newTestData.time_limit,
                maxRetries: newTestData.max_retries,
                retryCooldownMinutes: newTestData.retry_cooldown_minutes,
                displayOrder: newTestData.display_order,
                category: newTestData.category,
                passThreshold: newTestData.pass_threshold
              });

              // Load questions for the new copy
              await fetchQuestions(newTestData.id);

              // Show notification about the copy
              toast({
                title: "Test Copied",
                description: `Created your personal copy: "${newTestData.name}". You are now editing your copy.`,
                duration: 5000,
              });

              // Track test copy creation
              analytics.trackEvent('test_copied_on_edit', {
                category: 'content_management',
                original_test_id: selectedTest.id,
                new_test_id: data.newTestId,
                test_name: newTestData.name,
                user_role: teacherContext ? 'teacher' : 'admin',
                label: newTestData.name
              });
            } else {
              throw new Error('Failed to fetch new test details');
            }
          } catch (error) {
            console.error('Error fetching new test details after copy:', error);
            toast({
              title: "Test Copied",
              description: data.message || "Test copied successfully. Please refresh to see your copy.",
              duration: 5000,
            });
          }
        } else {
          // This was a direct edit - track normal test modification
          analytics.trackEvent('test_modified', {
            category: 'content_management',
            test_id: data.id,
            test_name: testDetails.name,
            modifier_role: teacherContext ? 'teacher' : 'admin',
            label: testDetails.name
          });
        }
      }
      
      await fetchTests();

      // Only show this toast if copy-on-edit didn't happen (which shows its own toast)
      if (!data.newTestId) {
        toast({
          title: "Success",
          description: `Test ${isNewTest ? 'created' : 'updated'} successfully`,
        });
      }

      setEditingTest(null);
      setEditAction(null);
      setSelectedTest(null); // Return to test list view
      setActiveTab("tests"); // Switch back to tests tab
    } catch (error) {
      handleError(error, `Error ${isNewTest ? 'creating' : 'updating'} test`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Check if we have a valid test selected
      if (!selectedTest || !selectedTest.id) {
        throw new Error('No test selected. Please save the test first before adding questions.');
      }
      // Format answers based on question type
      let formattedAnswers;
      if (newQuestion.question_type === 'true_false') {
        formattedAnswers = ['True', 'False'];
      } else if (newQuestion.question_type === 'written') {
        formattedAnswers = ['written_response'];
      } else {
        formattedAnswers = newQuestion.answers.split('\n').filter(answer => answer.trim() !== '');
      }
      
      const formattedQuestion = {
        question_text: newQuestion.question_text,
        answers: formattedAnswers,
        correct_answer: newQuestion.correct_answer,
        question_type: newQuestion.question_type,
        image_url: newQuestion.image_url
      };

      const url = editingQuestion
        ? `${process.env.REACT_APP_API_URL}/api/admin/questions/${editingQuestion.id}`
        : `${process.env.REACT_APP_API_URL}/api/admin/tests/${selectedTest.id}/questions`;
        
      const method = editingQuestion ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': authState?.sessionId
        },
        credentials: 'include',
        body: JSON.stringify(formattedQuestion),
      });

      if (!response.ok) {
        // Try to extract detailed error message from backend
        let errorMessage = 'Failed to save question';
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }

      const questionData = await response.json();

      // Always reset dialog states immediately after successful response
      setEditingQuestion(null);
      setNewQuestion({
        question_text: '',
        answers: '',
        correct_answer: '',
        question_type: 'multiple_choice',
        image_url: null
      });
      setAnswers([]);
      setIsAddingQuestion(false);
      setIsQuestionDialogOpen(false);

      // Show success notification for question addition
      toast({
        title: "Success",
        description: `Question ${editingQuestion ? 'updated' : 'added'} successfully`,
      });

      // Handle copy-on-edit: only if a REAL copy was made (different test IDs)
      let copyWasMade = false;
      if (questionData && questionData.wasCopied &&
          questionData.originalTestId && questionData.newTestId &&
          questionData.originalTestId !== questionData.newTestId) {

        copyWasMade = true;
        console.log(`Copy-on-edit detected for question ${editingQuestion ? 'update' : 'creation'}: switching to new test copy`, {
          originalTestId: questionData.originalTestId,
          newTestId: questionData.newTestId,
          originalQuestionId: editingQuestion?.id,
          newQuestionId: questionData.newQuestionId
        });

        try {
          // Fetch the new test data
          const newTestResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tests/${questionData.newTestId}`);
          if (newTestResponse.ok) {
            const newTestData = await newTestResponse.json();
            setSelectedTest(newTestData);

            // Update test details to reflect the new test
            setTestDetails({
              name: newTestData.name,
              description: newTestData.description,
              timeLimit: newTestData.time_limit,
              maxRetries: newTestData.max_retries,
              retryCooldownMinutes: newTestData.retry_cooldown_minutes,
              displayOrder: newTestData.display_order,
              category: newTestData.category,
              passThreshold: newTestData.pass_threshold
            });

            // Show notification about the copy
            toast({
              title: "Test Copied",
              description: `Created your personal copy: "${newTestData.name}". You are now editing your copy.`,
              duration: 5000,
            });

            // Refresh questions for the new test
            await fetchQuestions(questionData.newTestId);
          }
        } catch (copyError) {
          console.error('Copy-on-edit handling failed:', copyError);
          // Don't let copy errors affect the main flow
        }
      }

      // Always refresh questions for the current test (whether copied or not)
      const currentTestId = copyWasMade ? questionData.newTestId : selectedTest?.id;
      if (currentTestId) {
        try {
          await fetchQuestions(currentTestId);

          // Refresh test details
          const testResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tests/${currentTestId}`);
          if (testResponse.ok) {
            const testData = await testResponse.json();
            setSelectedTest(testData);
          }
        } catch (refreshError) {
          console.error('Failed to refresh test data:', refreshError);
        }
      }
    } catch (error) {
      handleError(error, 'Error saving question');
    } finally {
      // Guarantee dialog state reset regardless of success or failure
      setIsLoading(false);
      setIsAddingQuestion(false);
      setIsQuestionDialogOpen(false);
      setEditingQuestion(null);
    }
  };

  const handleDeleteQuestion = (questionId) => {
    // Find the question to show in confirmation dialog
    const question = questions.find(q => q.id === questionId);
    setQuestionToDelete(question);
    setIsDeleteQuestionDialogOpen(true);
  };

  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return;

    setIsLoading(true);

    // Store original questions for rollback if needed
    const originalQuestions = [...questions];

    // Optimistic UI update - immediately remove question from UI
    const updatedQuestions = questions.map(q =>
      q.id === questionToDelete.id
        ? { ...q, is_active: false }
        : q
    );
    setQuestions(updatedQuestions);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/questions/${questionToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'X-Session-Id': authState?.sessionId
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete question');
      }

      await response.json();

      // Force refresh questions to ensure backend state is synchronized
      await fetchQuestions(selectedTest.id);

      // Refresh test details
      const testResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tests/${selectedTest.id}`);
      if (testResponse.ok) {
        const testData = await testResponse.json();
        setSelectedTest(testData);
      }

      toast({
        title: "Success",
        description: "Question deleted successfully"
      });
    } catch (error) {
      // Rollback optimistic update on error
      setQuestions(originalQuestions);
      handleError(error, 'Error deleting question');
    } finally {
      setIsLoading(false);
      setIsDeleteQuestionDialogOpen(false);
      setQuestionToDelete(null);
    }
  };

  const handleDeleteTest = async () => {
    if (!selectedTest) return;
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tests/${selectedTest.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'X-Session-Id': authState?.sessionId
        }
      });

      if (!response.ok) throw new Error('Failed to delete test');

      toast({
        title: "Success",
        description: "Test deleted successfully"
      });
      
      setSelectedTest(null);
      fetchTests();
      setActiveTab("tests");
      setIsDeleteConfirmOpen(false);
    } catch (error) {
      handleError(error, 'Failed to delete test');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivateQuestion = async (questionId) => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/questions/${questionId}/reactivate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Session-Id': authState?.sessionId
        }
      });

      if (!response.ok) throw new Error('Failed to reactivate question');

      toast({
        title: "Success",
        description: "Question reactivated successfully"
      });
      
      fetchQuestions(selectedTest.id);
    } catch (error) {
      handleError(error, 'Failed to reactivate question');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTestFromTable = async (testId) => {
    setIsLoading(true);
    
    try {
      // Find the test to determine if teacher can delete it
      const test = tests.find(t => t.id === testId);
      if (!test) {
        throw new Error('Test not found');
      }

      // Check permissions
      if (!canDeleteTest(test)) {
        throw new Error('You can only delete tests you created');
      }

      const endpoint = getApiEndpoint('tests', testId);
        
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': authState?.sessionId
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete test');
      }

      // Force fresh data after deletion to prevent state corruption
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      fetchTests();

      toast({
        title: "Test Deleted",
        description: "Test moved to trash (can be restored for 30 days)",
      });
    } catch (error) {
      handleError(error, 'Failed to delete test');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestoreTest = async (testId) => {
    setIsLoading(true);
    
    try {
      const endpoint = teacherContext 
        ? `${process.env.REACT_APP_API_URL}/api/teacher/tests/${testId}/restore`
        : `${process.env.REACT_APP_API_URL}/api/admin/tests/${testId}/restore`;
        
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': authState?.sessionId
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to restore test');

      // Force fresh data after restore to prevent state corruption
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      fetchTests();

      toast({
        title: "Test Restored",
        description: "Test restored from trash successfully",
      });
    } catch (error) {
      handleError(error, 'Failed to restore test');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermanentDelete = async (testId) => {
    const testName = tests.find(t => t.id === testId)?.name || 'Unknown Test';
    
    if (!window.confirm(
      `⚠️ PERMANENT DELETION WARNING ⚠️\n\n` +
      `Are you sure you want to permanently delete "${testName}"?\n\n` +
      `This will:\n` +
      `• Remove the test completely from the database\n` +
      `• Delete all associated questions\n` +
      `• Remove all student test results\n` +
      `• Cannot be undone\n\n` +
      `Type YES in the next dialog to confirm.`
    )) {
      return;
    }
    
    const confirmation = window.prompt(
      `To confirm permanent deletion of "${testName}", please type: YES`
    );
    
    if (confirmation !== 'YES') {
      alert('Deletion cancelled. Test was not deleted.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tests/${testId}/permanent`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': authState?.sessionId
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to permanently delete test');

      fetchTests();

      toast({
        title: "Test Permanently Deleted",
        description: "Test has been permanently removed",
      });
    } catch (error) {
      handleError(error, 'Failed to permanently delete test');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestAssignment = async (testId, assignmentData) => {
    setIsLoading(true);
    
    try {
      logger.info('test_assignment_initiated', {
        testId,
        assignmentData,
        timestamp: new Date().toISOString()
      });

      // Use different endpoints for teachers vs admins
      const endpoint = teacherContext 
        ? `${process.env.REACT_APP_API_URL}/api/teacher/tests/cohort-assignments`
        : `${process.env.REACT_APP_API_URL}/api/admin/tests/cohort-assignments`;
      
      const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Id': authState?.sessionId
          },
          credentials: 'include',
          body: JSON.stringify({
            assignments: [{
              testId,
              cohortIds: assignmentData.cohortIds
            }]
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign test');
      }

      const data = await response.json();

      // Track test assignment 
      const testForAssignment = tests.find(t => String(t.id) === String(testId));
      if (testForAssignment) {
        analytics.trackTestAssignment(
          testId,
          testForAssignment.name,
          assignmentData.cohortIds[0], // Track first cohort for simplicity
          teacherContext ? 'teacher' : 'admin'
        );
      }

      logger.info('test_assignment_successful', {
        testId,
        cohortCount: assignmentData.cohortIds.length,
        response: data,
        timestamp: new Date().toISOString()
      });

      toast({
        title: "Success",
        description: 'The test has been assigned successfully',
      });

      setIsAssignmentDialogOpen(false);
      setAssigningTest(null);
      await fetchAssignments();
    } catch (error) {
      handleError(error, 'Failed to assign test');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecountTests = async () => {
    setIsRecounting(true);
    setLastRecountStatus(null);
    
    try {
      toast({
        title: "Processing",
        description: "Recounting all tests...",
      });
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/recount-all-tests`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-Id': authState?.sessionId
        },
        credentials: 'include'
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.details || 'Failed to recount tests');
      }

      logger.info('tests_recounted', {
        updatedCount: responseData.updates?.length || 0,
        timestamp: new Date().toISOString()
      });

      await fetchTests();

      setLastRecountStatus({
        success: true,
        message: `Successfully recounted ${responseData.updates?.length || 0} tests.`,
        details: responseData.updates
      });

      toast({
        title: "Success",
        description: `Successfully recounted ${responseData.updates?.length || 0} tests.`,
      });
    } catch (error) {
      setLastRecountStatus({
        success: false,
        message: error.message || 'Failed to recount tests.'
      });

      handleError(error, 'Failed to recount tests');
    } finally {
      setIsRecounting(false);
    }
  };

  const handleAnswersChange = (e) => {
    const answerList = e.target.value.split('\n').filter(answer => answer.trim() !== '');
    setNewQuestion({...newQuestion, answers: e.target.value});
    setAnswers(answerList);
  };

  // === Test Statistics ===
  
  const fetchTestStats = useCallback(async (testId) => {
    try {
      setIsLoading(true);
      
      // Fetch test statistics from the backend
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/tests/${testId}/stats`,
        {
          credentials: 'include',
          headers: {
            'X-Session-Id': authState?.sessionId
          }
        }
      );
      
      if (!response.ok) {
        // If stats endpoint doesn't exist, create basic stats from available data
        const test = tests.find(t => t.id === testId);
        const basicStats = {
          testName: test?.name || 'Unknown Test',
          totalQuestions: test?.total_questions || 0,
          totalAttempts: 0,
          averageScore: 0,
          passRate: 0,
          hardestQuestion: 'No data available',
          easiestQuestion: 'No data available',
          category: test?.category || 'Unknown',
          createdDate: test?.created_at ? new Date(test.created_at).toLocaleDateString() : 'Unknown',
          lastModified: test?.updated_at ? new Date(test.updated_at).toLocaleDateString() : 'Unknown'
        };
        setTestStats(basicStats);
        return;
      }
      
      const stats = await response.json();
      setTestStats(stats);
    } catch (error) {
      logger.error('test_stats_fetch_failed', {
        testId,
        error: error.message
      });
      
      // Fallback to basic stats
      const test = tests.find(t => t.id === testId);
      const basicStats = {
        testName: test?.name || 'Unknown Test',
        totalQuestions: test?.total_questions || 0,
        totalAttempts: 0,
        averageScore: 0,
        passRate: 0,
        hardestQuestion: 'No data available',
        easiestQuestion: 'No data available',
        category: test?.category || 'Unknown',
        createdDate: test?.created_at ? new Date(test.created_at).toLocaleDateString() : 'Unknown',
        lastModified: test?.updated_at ? new Date(test.updated_at).toLocaleDateString() : 'Unknown'
      };
      setTestStats(basicStats);
    } finally {
      setIsLoading(false);
    }
  }, [authState?.sessionId, tests]);

  const handleViewStats = async (test) => {
    setStatsTest(test);
    setIsStatsDialogOpen(true);
    await fetchTestStats(test.id);
  };

  // === Helper Functions ===
  
  const handleSort = (field) => {
    console.log('handleSort called with field:', field, 'current:', sortField, sortDirection);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortTests = (testsToSort) => {
    return [...testsToSort].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'category':
          aValue = a.category?.toLowerCase() || '';
          bValue = b.category?.toLowerCase() || '';
          break;
        case 'creator':
          aValue = a.creator_name?.toLowerCase() || '';
          bValue = b.creator_name?.toLowerCase() || '';
          break;
        case 'questions':
          aValue = a.total_questions || 0;
          bValue = b.total_questions || 0;
          break;
        case 'status':
          // Sort by: Published, Deleted
          aValue = a.deleted_at ? 1 : 0;
          bValue = b.deleted_at ? 1 : 0;
          break;
        default:
          aValue = a[sortField] || '';
          bValue = b[sortField] || '';
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  const filteredTests = sortTests(tests.filter(test => {
    // Apply search filter
    const matchesSearch = searchQuery === '' ||
      test.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply checkbox filters (trash-only system)
    let matchesTrashFilter = true;
    if (showTrash) {
      // "Show Deleted Only" - only show deleted tests
      matchesTrashFilter = test.deleted_at !== null;
    } else if (!showInactiveTests) {
      // Default behavior - only show non-deleted tests
      matchesTrashFilter = test.deleted_at === null;
    }
    // If showInactiveTests is true but showTrash is false, show both (no additional filter)
    
    // Apply status filter (trash-only system) - this works with the dropdown
    const matchesStatus =
      statusFilter === 'all' ? true :
      statusFilter === 'deleted' ? test.deleted_at !== null :
      true;
    
    // Apply category filter
    const matchesCategory = 
      categoryFilter === 'all' ||
      test.category === categoryFilter;
    
    // Apply owner filter
    const matchesOwner = 
      ownerFilter === 'all' ||
      (ownerFilter === 'admin' && test.owner_type === 'admin') ||
      (ownerFilter === 'teacher' && test.owner_type === 'teacher') ||
      (ownerFilter === 'mine' && currentUserId && test.creator_id === currentUserId);
    
    return matchesSearch && matchesTrashFilter && matchesStatus && matchesCategory && matchesOwner;
  }));

  const getAssignmentsByTest = (testId) => {
    return assignments.filter(a => a.test_id === testId);
  };

  // === UI Components ===
  
  const SortableTableHead = ({ field, children, className = "" }) => {
    const handleHeaderClick = () => {
      console.log('Header clicked:', field); // Debug log
      handleSort(field);
    };

    return (
      <TableHead className={className}>
        <div 
          className="cursor-pointer hover:bg-gray-50 select-none p-2 -m-2 rounded flex items-center justify-between"
          onClick={handleHeaderClick}
        >
          <span>{children}</span>
          <div className="flex flex-col ml-1">
            <ChevronUp 
              className={`h-3 w-3 ${sortField === field && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400'}`} 
            />
            <ChevronDown 
              className={`h-3 w-3 -mt-1 ${sortField === field && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400'}`} 
            />
          </div>
        </div>
      </TableHead>
    );
  };

  const QuestionItem = ({ question, index, onEdit, onDelete, onReactivate }) => (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-base">Question {index + 1}</CardTitle>
          {!question.is_active && (
            <Badge variant="outline" className="bg-gray-100">Inactive</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pb-2">
        <p className="font-medium mb-2">{question.question_text}</p>
        
        {question.image_url && (
          <div className="mb-3">
            <img 
              src={question.image_url.startsWith('http') ? question.image_url : `${process.env.REACT_APP_API_URL}${question.image_url}`}
              alt="Question illustration"
              className="max-w-full h-auto rounded border"
              style={{ maxHeight: '200px' }}
              onError={(e) => {
                console.error('Failed to load question image:', question.image_url);
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
        
        <div className="space-y-1 mb-4">
          {question.answers.map((answer, i) => (
            <div 
              key={i} 
              className={`p-2 rounded ${answer === question.correct_answer ? "bg-green-50 border border-green-200" : "bg-gray-50 border border-gray-200"}`}
            >
              <div className="flex items-center">
                {answer === question.correct_answer && (
                  <CheckCircle2 className="h-4 w-4 text-green-500 mr-2" />
                )}
                <span className={answer === question.correct_answer ? "font-medium" : ""}>
                  {answer}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter>
        <div className="flex space-x-2">
          <Button size="sm" variant="outline" onClick={() => onEdit(question)}>
            <Edit className="h-4 w-4 mr-1" /> Edit
          </Button>
          
          {question.is_active ? (
            <Button size="sm" variant="outline" className="text-red-500" onClick={() => onDelete(question.id)}>
              <Trash className="h-4 w-4 mr-1" /> Delete
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="text-green-500" onClick={() => onReactivate(question.id)}>
              <RotateCw className="h-4 w-4 mr-1" /> Reactivate
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );

  const TestAssignmentDialog = ({ isOpen, onClose, test, cohorts, onAssign }) => {
    const [selectedCohorts, setSelectedCohorts] = useState([]);
    const [dueDate, setDueDate] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    useEffect(() => {
      if (isOpen && test) {
        // Find existing assignments for this test
        const existingAssignments = assignments.filter(a => a.test_id === test.id);
        setSelectedCohorts(existingAssignments.map(a => a.cohort_id));
        setDueDate('');
        setIsSubmitting(false);
      }
    }, [isOpen, test]);

    const handleAssign = async () => {
      try {
        setIsSubmitting(true);

        logger.info('admin_test_assignment_update_triggered', {
          testId: test.id,
          selectedCohorts,
          dueDate,
          timestamp: new Date().toISOString()
        });

        await onAssign(test.id, {
          cohortIds: selectedCohorts,
          dueDate: dueDate || null
        });
    
      } catch (error) {
        handleError(error, 'Failed to update test assignments');
      } finally {
        setIsSubmitting(false);
      }
    };

    // Get test statistics for the mini manager
    const getTestStats = () => {
      if (!test) return null;
      
      const testAssignments = getAssignmentsByTest(test.id);
      const assignedClasses = testAssignments.length;
      
      return {
        totalQuestions: test.total_questions || 0,
        timeLimit: test.time_limit || 60,
        maxRetries: test.max_retries || 0,
        assignedClasses,
        category: test.category || 'woodshop',
        isActive: test.is_active
      };
    };

    const testStats = getTestStats();

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle>Manage Test Assignments</DialogTitle>
            <DialogDescription>
              Assign "{test?.name}" to classes and view test details
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Left Panel - Assignment Controls */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-base font-medium">Assignment Settings</Label>
                <Separator />
              </div>
              
              <div className="grid gap-2">
                <Label>Select Classes ({selectedCohorts.length} selected)</Label>
                <div className="max-h-48 overflow-y-auto border rounded-md p-2">
                  {cohorts.map((cohort) => (
                    <div key={cohort.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
                      <Checkbox
                        id={`cohort-${cohort.id}`}
                        checked={selectedCohorts.includes(cohort.id)}
                        onCheckedChange={(checked) => {
                          setSelectedCohorts(prev => 
                            checked 
                              ? [...prev, cohort.id]
                              : prev.filter(id => id !== cohort.id)
                          );
                        }}
                      />
                      <Label htmlFor={`cohort-${cohort.id}`} className="cursor-pointer">
                        {cohort.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="dueDate">Due Date (Optional)</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            {/* Right Panel - Mini Test Manager */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-base font-medium">Test Overview</Label>
                <Separator />
              </div>
              
              {testStats && (
                <div className="space-y-3">
                  {/* Test Summary Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <Card className="p-3">
                      <div className="text-xs text-muted-foreground">Questions</div>
                      <div className="text-lg font-semibold">{testStats.totalQuestions}</div>
                    </Card>
                    <Card className="p-3">
                      <div className="text-xs text-muted-foreground">Time Limit</div>
                      <div className="text-lg font-semibold">{testStats.timeLimit}m</div>
                    </Card>
                    <Card className="p-3">
                      <div className="text-xs text-muted-foreground">Max Retries</div>
                      <div className="text-lg font-semibold">
                        {testStats.maxRetries === 0 ? '∞' : testStats.maxRetries}
                      </div>
                    </Card>
                    <Card className="p-3">
                      <div className="text-xs text-muted-foreground">Category</div>
                      <div className="text-sm font-medium capitalize">{testStats.category}</div>
                    </Card>
                  </div>

                  {/* Test Status */}
                  <Card className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-muted-foreground">Current Status</div>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant={testStats.isActive ? "default" : "secondary"}>
                            {testStats.isActive ? "Active" : "Inactive"}
                          </Badge>
                          {testStats.assignedClasses > 0 && (
                            <Badge variant="outline" className="text-xs">
                              Assigned to {testStats.assignedClasses} {testStats.assignedClasses === 1 ? 'class' : 'classes'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Quick Actions */}
                  <Card className="p-3">
                    <div className="text-xs text-muted-foreground mb-2">Quick Actions</div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          onClose();
                          handleTestEdit(test);
                        }}
                        className="text-xs"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit Test
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          onClose();
                          handleViewStats(test);
                        }}
                        className="text-xs"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View Stats
                      </Button>
                      {canShareTest(test) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            onClose();
                            handleShareTest(test);
                          }}
                          className="text-xs"
                        >
                          <Share2 className="h-3 w-3 mr-1" />
                          Share
                        </Button>
                      )}
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Assignments'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const TestStatsDialog = ({ isOpen, onClose, test, stats }) => (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Info className="h-5 w-5 text-blue-600" />
            <span>Test Statistics</span>
          </DialogTitle>
          <DialogDescription>
            Detailed information and statistics for "{stats?.testName || test?.name}"
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Category</div>
              <div className="text-lg font-semibold capitalize">{stats?.category || 'Unknown'}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Total Questions</div>
              <div className="text-lg font-semibold">{stats?.totalQuestions || 0}</div>
            </Card>
          </div>
          
          {/* Performance Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Total Attempts</div>
              <div className="text-lg font-semibold text-blue-600">{stats?.totalAttempts || 0}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Average Score</div>
              <div className="text-lg font-semibold text-green-600">{stats?.averageScore || 0}%</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Pass Rate</div>
              <div className="text-lg font-semibold text-purple-600">{stats?.passRate || 0}%</div>
            </Card>
          </div>
          
          {/* Question Analysis */}
          <Card className="p-4">
            <h4 className="font-medium mb-3">Question Analysis</h4>
            <div className="space-y-3">
              <div>
                <div className="text-sm text-muted-foreground">Hardest Question</div>
                <div className="text-sm font-medium text-red-600">{stats?.hardestQuestion || 'No data available'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Easiest Question</div>
                <div className="text-sm font-medium text-green-600">{stats?.easiestQuestion || 'No data available'}</div>
              </div>
            </div>
          </Card>
          
          {/* Metadata */}
          <Card className="p-4">
            <h4 className="font-medium mb-3">Test Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Created</div>
                <div className="font-medium">{formatRelativeTimeClean(stats?.createdTimestamp)}</div>
                <div className="text-xs text-muted-foreground">{stats?.createdDate || 'Unknown'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Last Modified</div>
                <div className="font-medium">{formatRelativeTimeClean(stats?.lastModifiedTimestamp)}</div>
                <div className="text-xs text-muted-foreground">{stats?.lastModified || 'Unknown'}</div>
              </div>
            </div>
          </Card>
        </div>
        
        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );


  const TestListActionMenu = ({ test, onEdit, onAssign, onShare, onDelete, onRestore, onPermanentDelete, onViewStats }) => (
    <div className="flex items-center space-x-1">
      {/* View Details Button - Opens stats dialog */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onViewStats(test)}
        className="h-8 px-2 text-blue-600 hover:text-blue-700"
        title="View test statistics"
      >
        <Eye className="h-4 w-4" />
      </Button>

      {/* Edit Button - Takes user to test editing view */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleTestEdit(test)}
        className="h-8 px-2"
        title="Edit test details and questions"
      >
        <Edit className="h-4 w-4" />
      </Button>

      {/* Assign Button - Only for active tests */}
      {!test.deleted_at && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onAssign(test)}
          className="h-8 px-2 text-green-600 hover:text-green-700"
          title="Assign to classes"
        >
          <Users className="h-4 w-4" />
        </Button>
      )}

      {/* Share Button - Only for tests that can be shared */}
      {!test.deleted_at && canShareTest(test) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onShare(test)}
          className="h-8 px-2 text-purple-600 hover:text-purple-700"
          title="Share with teachers"
        >
          <Share2 className="h-4 w-4" />
        </Button>
      )}

      {/* Delete/Restore Actions */}
      {test.deleted_at ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRestore(test.id)}
            className="h-8 px-2 text-green-600 hover:text-green-700"
            title="Restore test"
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPermanentDelete(test.id)}
            className="h-8 px-2 text-red-600 hover:text-red-700"
            title="Delete permanently"
          >
            <Trash className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(test.id)}
          className="h-8 px-2 text-red-600 hover:text-red-700"
          title="Move to trash"
        >
          <Trash className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  // === Main Component Render ===

  if (!authState) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 min-h-screen">
      <div className="p-4 lg:p-6 max-w-full">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
          <div>
            <h2 className="text-xl lg:text-2xl font-semibold">Test Manager</h2>
            <p className="text-sm text-muted-foreground mt-1">Create and manage tests and questions</p>
          </div>
          
          <div className="flex gap-2 w-full sm:w-auto">
            {!teacherContext && (
              <Button 
                variant="outline"
                onClick={handleRecountTests}
                disabled={isRecounting}
                className="flex-1 sm:flex-none"
                title="Recount all test questions"
              >
                {isRecounting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCw className="mr-2 h-4 w-4" />
                )}
                {isRecounting ? 'Counting...' : 'Recount Tests'}
              </Button>
            )}
            
            <Button 
              variant="outline"
              onClick={() => setIsAddCategoryDialogOpen(true)}
              className="flex-1 sm:flex-none"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Category
            </Button>
            
            <Button 
              onClick={() => handleTestSelect('new')}
              className="flex-1 sm:flex-none"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Create Test
            </Button>
          </div>
        </div>


      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 bg-gray-100 p-1 rounded-lg inline-flex">
          <TabsTrigger 
            value="tests"
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2 px-4"
          >
            Tests
          </TabsTrigger>
          <TabsTrigger 
            value="order"
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2 px-4"
          >
            Test Order
          </TabsTrigger>
          {selectedTest && (
            <TabsTrigger 
              value="details"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2 px-4"
            >
              {isNewTest ? "New Test" : selectedTest.name}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="tests">
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Tests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{tests.length}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {tests.filter(t => !t.deleted_at).length} active • {tests.filter(t => t.deleted_at).length} deleted
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">My Tests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {tests.filter(t => teacherContext ? t.creator_id === teacherId : t.owner_type === 'admin').length}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Tests you created
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Shared Tests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {teacherContext ? tests.filter(t => t.owner_type === 'admin' || (t.creator_id !== teacherId && !t.deleted_at)).length : tests.filter(t => t.owner_type === 'teacher').length}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {teacherContext ? 'Available to you' : 'Created by teachers'}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {new Set(tests.filter(t => !t.deleted_at).map(t => t.category || 'woodshop')).size}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Different test types
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Quick Filters */}
          <Card className="mb-4">
            <CardHeader className="pb-4">
              <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                <div>
                  <CardTitle className="text-lg">Test Library</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage your safety tests and assessments
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    placeholder="Search tests..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full sm:w-[200px]"
                  />
                  
                  <Select 
                    value={categoryFilter} 
                    onValueChange={setCategoryFilter}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="woodshop">Woodshop</SelectItem>
                      <SelectItem value="auto">Auto Shop</SelectItem>
                      <SelectItem value="metal">Metal Shop</SelectItem>
                      <SelectItem value="general">General Safety</SelectItem>
                      {customCategories.map(category => (
                        <SelectItem key={category} value={category} className="capitalize">
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Quick Filter Buttons */}
              <div className="flex flex-wrap gap-2 pt-4">
                <Button
                  variant={ownerFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOwnerFilter('all')}
                >
                  All Tests
                </Button>
                <Button
                  variant={ownerFilter === 'mine' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOwnerFilter(ownerFilter === 'mine' ? 'all' : 'mine')}
                >
                  My Tests
                </Button>
                <Button
                  variant={ownerFilter === 'admin' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOwnerFilter(ownerFilter === 'admin' ? 'all' : 'admin')}
                >
                  Admin Tests
                </Button>
                {teacherContext && (
                  <Button
                    variant={ownerFilter === 'teacher' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setOwnerFilter(ownerFilter === 'teacher' ? 'all' : 'teacher')}
                  >
                    Teacher Tests
                  </Button>
                )}
                <Button
                  variant={showTrash ? 'destructive' : 'outline'}
                  size="sm"
                  onClick={() => setShowTrash(!showTrash)}
                >
                  {showTrash ? 'Hide' : 'Show'} Deleted
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    localStorage.removeItem(LOCAL_STORAGE_KEY);
                    fetchTests();
                    toast({
                      title: "Refreshed",
                      description: "Test data refreshed successfully"
                    });
                  }}
                  className="ml-2"
                >
                  <RefreshCcw className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
                
                <div className="flex items-center space-x-2 ml-auto">
                  <span className="text-sm text-muted-foreground">
                    {filteredTests.length} tests
                  </span>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead field="name" className="w-[26%]">Name</SortableTableHead>
                      <SortableTableHead field="category" className="w-[12%]">Category</SortableTableHead>
                      <SortableTableHead field="creator" className="w-[12%]">Creator</SortableTableHead>
                      <SortableTableHead field="questions" className="w-[10%]">Questions</SortableTableHead>
                      <SortableTableHead field="status" className="w-[12%]">Status</SortableTableHead>
                      <TableHead className="w-[12%]">Assigned To</TableHead>
                      <TableHead className="w-[16%]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-4">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : filteredTests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                          {searchQuery || statusFilter !== 'all' 
                            ? "No tests match your search criteria" 
                            : "No tests available"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTests.map((test) => {
                        const testAssignments = getAssignmentsByTest(test.id);
                        
                        return (
                          <TableRow key={test.id} className={!test.is_active ? "bg-gray-50" : undefined}>
                            <TableCell>
                              <div className="font-medium">{test.name}</div>
                              {test.description && (
                                <div className="text-sm text-muted-foreground truncate max-w-[300px]">
                                  {test.description}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {test.category || 'woodshop'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {test.creator_name || 'Unknown'}
                                <div className="text-xs text-muted-foreground">
                                  {test.owner_type === 'admin' ? 'Admin' : 'Teacher'}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {test.total_questions || 0}
                            </TableCell>
                            <TableCell>
                              {test.deleted_at ? (
                                <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300">Deleted</Badge>
                              ) : testAssignments.length > 0 ? (
                                <Badge variant="default" className="bg-green-100 text-green-800 border-green-300">Assigned</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">Unassigned</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {testAssignments.length > 0 ? (
                                <div className="flex items-center">
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                    {testAssignments.length} {testAssignments.length === 1 ? 'class' : 'classes'}
                                  </Badge>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">Not assigned</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <TestListActionMenu 
                                test={test}
                                onEdit={() => handleTestEdit(test)}
                                onAssign={() => {
                                  setAssigningTest(test);
                                  setIsAssignmentDialogOpen(true);
                                }}
                                onShare={() => handleShareTest(test)}
                                onDelete={handleDeleteTestFromTable}
                                onRestore={handleRestoreTest}
                                onPermanentDelete={handlePermanentDelete}
                                onViewStats={() => handleViewStats(test)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details">
          {(selectedTest || isNewTest) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="md:col-span-1">
                <CardHeader>
                  <CardTitle>{isNewTest ? "Create New Test" : "Edit Test"}</CardTitle>
                  {!isNewTest && (
                    <CardDescription>
                      {formatRelativeTime(selectedTest.updated_at || selectedTest.created_at)}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleTestSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Test Name</Label>
                      <Input
                        id="name"
                        value={testDetails.name}
                        onChange={(e) => setTestDetails({...testDetails, name: e.target.value})}
                        required
                        placeholder="Enter test name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={testDetails.description || ''}
                        onChange={(e) => setTestDetails({...testDetails, description: e.target.value})}
                        placeholder="Enter test description"
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={testDetails.category}
                        onValueChange={(value) => setTestDetails({...testDetails, category: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="woodshop">Woodshop</SelectItem>
                          <SelectItem value="auto">Auto Shop</SelectItem>
                          <SelectItem value="metal">Metal Shop</SelectItem>
                          <SelectItem value="general">General Safety</SelectItem>
                          {customCategories.map(category => (
                            <SelectItem key={category} value={category} className="capitalize">
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="timeLimit">Time Limit (minutes)</Label>
                        <Input
                          id="timeLimit"
                          type="number"
                          value={testDetails.timeLimit}
                          onChange={(e) => setTestDetails({...testDetails, timeLimit: parseInt(e.target.value)})}
                          required
                          min={1}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="maxRetries">Max Retries (0 = unlimited)</Label>
                        <Input
                          id="maxRetries"
                          type="number"
                          value={testDetails.maxRetries}
                          onChange={(e) => setTestDetails({...testDetails, maxRetries: parseInt(e.target.value)})}
                          required
                          min={0}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="retryCooldown">Retry Cooldown After Failure</Label>
                      <Select
                        value={testDetails.retryCooldownMinutes?.toString()}
                        onValueChange={(value) => setTestDetails({...testDetails, retryCooldownMinutes: parseInt(value)})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select cooldown period" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">No cooldown (immediate retry)</SelectItem>
                          <SelectItem value="1">1 minute</SelectItem>
                          <SelectItem value="2">2 minutes</SelectItem>
                          <SelectItem value="5">5 minutes (default)</SelectItem>
                          <SelectItem value="10">10 minutes</SelectItem>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                          <SelectItem value="-1">Disable retries after failure</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Time students must wait before retrying after failing this test
                      </p>
                    </div>
                      
                    <div className="space-y-2">
                      <Label htmlFor="passThreshold">Pass Threshold (%)</Label>
                      <Input
                        id="passThreshold"
                        type="number"
                        step="0.01"
                        value={testDetails.passThreshold}
                        onChange={(e) => setTestDetails({...testDetails, passThreshold: parseFloat(e.target.value)})}
                        required
                        min={0}
                        max={100}
                        placeholder="90.00"
                      />
                      <p className="text-xs text-gray-500">Minimum percentage required to pass this test</p>
                    </div>
                    
                    {/* Display Order field removed - use Test Order tab instead */}
                    
                    <div className="flex justify-between mt-6">
                      <Button type="submit" disabled={isLoading}>
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          isNewTest ? 'Create Test' : 'Save'
                        )}
                      </Button>
                      
                      {!isNewTest && (
                        <Button 
                          type="button" 
                          variant="destructive" 
                          onClick={() => setIsDeleteConfirmOpen(true)}
                          disabled={isLoading}
                        >
                          Delete Test
                        </Button>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>

              {selectedTest && !isNewTest && (
                <Card className="md:col-span-2">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Test Questions</CardTitle>
                      <CardDescription>
                        {questions.filter(q => q.is_active).length} active questions
                      </CardDescription>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="showInactiveQuestions" 
                          checked={showInactiveQuestions}
                          onCheckedChange={setShowInactiveQuestions}
                        />
                        <Label htmlFor="showInactiveQuestions" className="text-sm">
                          Show Inactive
                        </Label>
                      </div>
                      
                      <Button 
                        onClick={() => {
                          console.log('=== ADD QUESTION BUTTON CLICKED ===');
                          console.log('selectedTest:', selectedTest);
                          console.log('selectedTest.id:', selectedTest?.id);
                          console.log('Current isAddingQuestion:', isAddingQuestion);
                          
                          setEditingQuestion(null);
                          setNewQuestion({ 
                            question_text: '', 
                            answers: '', 
                            correct_answer: '',
                            question_type: 'multiple_choice',
                            image_url: null
                          });
                          setAnswers([]);
                          resetDynamicAnswers();
                          setIsAddingQuestion(true);
                          
                          console.log('Set isAddingQuestion to true');
                        }}
                        disabled={!selectedTest || !selectedTest.id}
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        {!selectedTest || !selectedTest.id ? 'Save Test First' : 'Add Question'}
                      </Button>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {isLoading ? (
                      <div className="py-8 text-center">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading questions...</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Inline Question Creation Form - Only show when adding new questions, not editing */}
                        {console.log('=== CHECKING isAddingQuestion STATE ===', isAddingQuestion)}
                        {isAddingQuestion && !editingQuestion && (
                          <Card className="border-dashed border-2 border-blue-200 bg-blue-50/30">
                            <CardHeader>
                              <CardTitle className="text-lg text-blue-800">
                                {editingQuestion ? 'Edit Question' : 'Add New Question'}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <form onSubmit={handleQuestionSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label htmlFor="question-type">Question Type</Label>
                                    <Select
                                      value={newQuestion.question_type}
                                      onValueChange={(value) => {
                                        setNewQuestion({...newQuestion, question_type: value, correct_answer: '', answers: ''});
                                        resetDynamicAnswers();
                                      }}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select question type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {getQuestionTypes().map(type => (
                                          <SelectItem key={type.value} value={type.value}>
                                            {type.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <Label>Image Upload (Optional)</Label>
                                    <ImageSelector
                                      currentImageUrl={newQuestion.image_url}
                                      onImageSelect={(imageUrl) => {
                                        setNewQuestion({...newQuestion, image_url: imageUrl});
                                      }}
                                      onImageRemove={() => {
                                        setNewQuestion({...newQuestion, image_url: null});
                                      }}
                                      teacherContext={teacherContext}
                                      shouldRefreshOnFocus={shouldRefreshOnFocus}
                                    />
                                  </div>
                                </div>
                                
                                <div className="space-y-2">
                                  <Label htmlFor="question-text">Question</Label>
                                  <Textarea
                                    id="question-text"
                                    value={newQuestion.question_text}
                                    onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})}
                                    required
                                    placeholder="Enter your question"
                                    className="min-h-[80px]"
                                  />
                                </div>
                                
                                {/* Dynamic Question Template Based on Type */}
                                {newQuestion.question_type === 'multiple_choice' && (
                                  <>
                                    <div className="space-y-2">
                                      <Label>Answer Options</Label>
                                      <div className="space-y-3">
                                        {dynamicAnswers.map((answer, index) => (
                                          <div key={index} className="flex items-center space-x-2">
                                            <span className="text-sm text-muted-foreground w-8">{index + 1}.</span>
                                            <Input
                                              value={answer}
                                              onChange={(e) => handleDynamicAnswerChange(index, e.target.value)}
                                              placeholder={`Answer option ${index + 1}`}
                                              className="flex-1"
                                            />
                                          </div>
                                        ))}
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        Start typing to add more answer options automatically.
                                      </p>
                                    </div>
                                    
                                    <div className="space-y-2">
                                      <Label htmlFor="correct-answer">Correct Answer</Label>
                                      <Select
                                        value={newQuestion.correct_answer}
                                        onValueChange={(value) => setNewQuestion({...newQuestion, correct_answer: value})}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select the correct answer" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {answers.length === 0 ? (
                                            <SelectItem value="placeholder_empty" disabled>
                                              Add answer options first
                                            </SelectItem>
                                          ) : (
                                            answers.map((answer, index) => (
                                              <SelectItem key={index} value={answer}>
                                                {answer}
                                              </SelectItem>
                                            ))
                                          )}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </>
                                )}
                            
                                {newQuestion.question_type === 'true_false' && (
                                  <div className="space-y-2">
                                    <Label htmlFor="correct-answer">Correct Answer</Label>
                                    <Select
                                      value={newQuestion.correct_answer}
                                      onValueChange={(value) => {
                                        setNewQuestion({...newQuestion, correct_answer: value, answers: 'True\nFalse'});
                                        setAnswers(['True', 'False']);
                                      }}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select the correct answer" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="True">True</SelectItem>
                                        <SelectItem value="False">False</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                                
                                {newQuestion.question_type === 'written' && (
                                  <div className="space-y-2">
                                    <Label htmlFor="sample-answer">Sample Answer (Optional)</Label>
                                    <Textarea
                                      id="sample-answer"
                                      value={newQuestion.correct_answer}
                                      onChange={(e) => {
                                        setNewQuestion({...newQuestion, correct_answer: e.target.value, answers: 'written_response'});
                                        setAnswers(['written_response']);
                                      }}
                                      placeholder="Provide a sample or expected answer"
                                      className="min-h-[80px]"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      This will be used as a reference for grading written responses.
                                    </p>
                                  </div>
                                )}
                                
                                <div className="flex gap-2 pt-4">
                                  <Button type="submit" disabled={
                                    isLoading || 
                                    !newQuestion.question_text.trim() || 
                                    !newQuestion.correct_answer.trim() ||
                                    (newQuestion.question_type === 'multiple_choice' && answers.length === 0)
                                  }>
                                    {isLoading ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                      </>
                                    ) : (
                                      editingQuestion ? 'Update Question' : 'Add Question'
                                    )}
                                  </Button>
                                  <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={() => {
                                      setIsAddingQuestion(false);
                                      setEditingQuestion(null);
                                      setNewQuestion({ 
                                        question_text: '', 
                                        answers: '', 
                                        correct_answer: '',
                                        question_type: 'multiple_choice',
                                        image_url: null
                                      });
                                      setAnswers([]);
                                      resetDynamicAnswers();
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </form>
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* No Questions Placeholder */}
                        {questions.length === 0 && !isAddingQuestion && (
                          <div className="py-12 text-center border rounded-md bg-gray-50">
                            <BookCopy className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium mb-1">No Questions Yet</h3>
                            <p className="text-muted-foreground mb-4">
                              This test doesn't have any questions yet.
                            </p>
                            <Button 
                              onClick={() => {
                                console.log('=== ADD FIRST QUESTION BUTTON CLICKED ===');
                                setEditingQuestion(null);
                                setNewQuestion({ 
                                  question_text: '', 
                                  answers: '', 
                                  correct_answer: '',
                                  question_type: 'multiple_choice',
                                  image_url: null
                                });
                                setAnswers([]);
                                resetDynamicAnswers();
                                setIsAddingQuestion(true);
                              }}
                              disabled={!selectedTest || !selectedTest.id}
                            >
                              <PlusCircle className="h-4 w-4 mr-2" />
                              {!selectedTest || !selectedTest.id ? 'Save Test First' : 'Add First Question'}
                            </Button>
                          </div>
                        )}
                        
                        {/* Only filter by is_active if showInactiveQuestions is false */}
                        {questions.filter(q => showInactiveQuestions ? true : q.is_active)
                          .map((question, index) => {
                            // If this specific question is being edited, show the edit form instead
                            if (editingQuestion && editingQuestion.id === question.id && isAddingQuestion) {
                              // Use the EXACT same working form from the top, just moved here
                              return (
                                <Card key={question.id} className="border-dashed border-2 border-blue-200 bg-blue-50/30">
                                  <CardHeader>
                                    <CardTitle className="text-lg text-blue-800">
                                      Edit Question
                                    </CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    <form onSubmit={handleQuestionSubmit} className="space-y-4">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                          <Label htmlFor="question-type">Question Type</Label>
                                          <Select
                                            value={newQuestion.question_type}
                                            onValueChange={(value) => {
                                              setNewQuestion({...newQuestion, question_type: value, correct_answer: '', answers: ''});
                                              resetDynamicAnswers();
                                            }}
                                          >
                                            <SelectTrigger>
                                              <SelectValue placeholder="Select question type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {getQuestionTypes().map(type => (
                                                <SelectItem key={type.value} value={type.value}>
                                                  {type.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        
                                        <div className="space-y-2">
                                          <Label>Image Upload (Optional)</Label>
                                          <ImageSelector
                                            currentImageUrl={newQuestion.image_url}
                                            onImageSelect={(imageUrl) => {
                                              setNewQuestion({...newQuestion, image_url: imageUrl});
                                            }}
                                            onImageRemove={() => {
                                              setNewQuestion({...newQuestion, image_url: null});
                                            }}
                                            teacherContext={teacherContext}
                                            shouldRefreshOnFocus={shouldRefreshOnFocus}
                                          />
                                        </div>
                                      </div>
                                      
                                      <div className="space-y-2">
                                        <Label htmlFor="question-text">Question</Label>
                                        <Textarea
                                          id="question-text"
                                          value={newQuestion.question_text}
                                          onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})}
                                          required
                                          placeholder="Enter your question"
                                          className="min-h-[80px]"
                                        />
                                      </div>
                                      
                                      {/* Dynamic Question Template Based on Type */}
                                      {newQuestion.question_type === 'multiple_choice' && (
                                        <>
                                          <div className="space-y-2">
                                            <Label>Answer Options</Label>
                                            <div className="space-y-3">
                                              {dynamicAnswers.map((answer, index) => (
                                                <div key={index} className="flex items-center space-x-2">
                                                  <span className="text-sm text-muted-foreground w-8">{index + 1}.</span>
                                                  <Input
                                                    value={answer}
                                                    onChange={(e) => handleDynamicAnswerChange(index, e.target.value)}
                                                    placeholder={`Answer option ${index + 1}`}
                                                    className="flex-1"
                                                  />
                                                </div>
                                              ))}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                              Start typing to add more answer options automatically.
                                            </p>
                                          </div>
                                          
                                          <div className="space-y-2">
                                            <Label htmlFor="correct-answer">Correct Answer</Label>
                                            <Select
                                              value={newQuestion.correct_answer}
                                              onValueChange={(value) => setNewQuestion({...newQuestion, correct_answer: value})}
                                            >
                                              <SelectTrigger>
                                                <SelectValue placeholder="Select the correct answer" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {answers.length === 0 ? (
                                                  <SelectItem value="placeholder_empty" disabled>
                                                    Add answer options first
                                                  </SelectItem>
                                                ) : (
                                                  answers.map((answer, index) => (
                                                    <SelectItem key={index} value={answer}>
                                                      {answer}
                                                    </SelectItem>
                                                  ))
                                                )}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </>
                                      )}

                                      {newQuestion.question_type === 'true_false' && (
                                        <div className="space-y-2">
                                          <Label htmlFor="correct-answer">Correct Answer</Label>
                                          <Select
                                            value={newQuestion.correct_answer}
                                            onValueChange={(value) => {
                                              setNewQuestion({...newQuestion, correct_answer: value, answers: 'True\nFalse'});
                                              setAnswers(['True', 'False']);
                                            }}
                                          >
                                            <SelectTrigger>
                                              <SelectValue placeholder="Select the correct answer" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="True">True</SelectItem>
                                              <SelectItem value="False">False</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      )}
                                      
                                      {newQuestion.question_type === 'written' && (
                                        <div className="space-y-2">
                                          <Label htmlFor="sample-answer">Sample Answer (Optional)</Label>
                                          <Textarea
                                            id="sample-answer"
                                            value={newQuestion.correct_answer}
                                            onChange={(e) => {
                                              setNewQuestion({...newQuestion, correct_answer: e.target.value, answers: 'written_response'});
                                              setAnswers(['written_response']);
                                            }}
                                            placeholder="Provide a sample or expected answer"
                                            className="min-h-[80px]"
                                          />
                                          <p className="text-xs text-muted-foreground">
                                            This will be used as a reference for grading written responses.
                                          </p>
                                        </div>
                                      )}
                                      
                                      <div className="flex gap-2 pt-4">
                                        <Button type="submit" disabled={
                                          isLoading || 
                                          !newQuestion.question_text.trim() || 
                                          !newQuestion.correct_answer.trim() ||
                                          (newQuestion.question_type === 'multiple_choice' && answers.length === 0)
                                        }>
                                          {isLoading ? (
                                            <>
                                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                              Saving...
                                            </>
                                          ) : (
                                            editingQuestion ? 'Update Question' : 'Add Question'
                                          )}
                                        </Button>
                                        <Button 
                                          type="button" 
                                          variant="outline" 
                                          onClick={() => {
                                            setIsAddingQuestion(false);
                                            setEditingQuestion(null);
                                            setNewQuestion({ 
                                              question_text: '', 
                                              answers: '', 
                                              correct_answer: '',
                                              question_type: 'multiple_choice',
                                              image_url: null
                                            });
                                            setAnswers([]);
                                            resetDynamicAnswers();
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </form>
                                  </CardContent>
                                </Card>
                              );
                            }
                            
                            // Otherwise show the normal question item
                            return (
                              <QuestionItem 
                                key={question.id}
                                question={question}
                                index={index}
                                onEdit={(question) => {
                                  setEditingQuestion(question);
                                  setNewQuestion({
                                    question_text: question.question_text,
                                    answers: question.answers.join('\n'),
                                    correct_answer: question.correct_answer,
                                    question_type: question.question_type || 'multiple_choice',
                                    image_url: question.image_url || null
                                  });
                                  // Set up dynamic answers for editing
                                  if (question.question_type === 'multiple_choice') {
                                    const answerArray = question.answers || [];
                                    setDynamicAnswers([...answerArray, '']); // Add empty field for potential new answers
                                    setAnswers(answerArray);
                                  } else {
                                    setAnswers(question.answers || []);
                                  }
                                  setIsAddingQuestion(true); // Enable editing mode
                                }}
                                onDelete={handleDeleteQuestion}
                                onReactivate={handleReactivateQuestion}
                              />
                            );
                          })
                        }
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="order">
          <Card>
            <CardHeader>
              <CardTitle>Manage Test Display Order</CardTitle>
              <CardDescription>
                Drag tests to reorder or use the arrow buttons. Tests appear to students in this order.
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {orderedTests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No active tests to reorder</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="space-y-2">
                    {orderedTests.map((test, index) => (
                      <div 
                        key={test.id} 
                        draggable={!isReordering}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        className="flex items-center justify-between p-4 border rounded-lg bg-white hover:bg-gray-50 transition-colors cursor-move select-none"
                        title="Drag to reorder or use arrow buttons"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="flex flex-col text-sm text-muted-foreground">
                            <span className="font-mono font-medium">
                              #{index + 1}
                            </span>
                          </div>
                          
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">
                              {test.name}
                            </h4>
                            <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                              <span className="capitalize">
                                {test.category || 'woodshop'}
                              </span>
                              <span>
                                {test.total_questions || 0} questions
                              </span>
                              {test.time_limit && (
                                <span>
                                  {test.time_limit} min limit
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMoveTestUp(index)}
                            disabled={index === 0 || isReordering}
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMoveTestDown(index)}
                            disabled={index === orderedTests.length - 1 || isReordering}
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-end pt-4 border-t">
                    <Button 
                      onClick={handleSaveTestOrder}
                      disabled={isReordering}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isReordering ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving Order...
                        </>
                      ) : (
                        'Save Order'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Question Editor Dialog */}
      <Dialog open={isQuestionDialogOpen} onOpenChange={setIsQuestionDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion ? 'Edit Question' : 'Add New Question'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleQuestionSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="question-text">Question</Label>
              <Textarea
                id="question-text"
                value={newQuestion.question_text}
                onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})}
                required
                placeholder="Enter your question"
                className="min-h-[80px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="answers">
                Answer Options (one per line)
              </Label>
              <Textarea
                id="answers"
                value={newQuestion.answers}
                onChange={handleAnswersChange}
                required
                placeholder="Enter each answer on a new line"
                className="min-h-[120px]"
              />
              <p className="text-xs text-muted-foreground">
                Enter each possible answer on a separate line.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="correct-answer">Correct Answer</Label>
              <Select
                value={newQuestion.correct_answer}
                onValueChange={(value) => setNewQuestion({...newQuestion, correct_answer: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select the correct answer" />
                </SelectTrigger>
                <SelectContent>
                  {answers.length === 0 ? (
                    <SelectItem value="placeholder_empty" disabled>
                      Add answer options first
                    </SelectItem>
                  ) : (
                    answers.map((answer, index) => (
                      <SelectItem key={index} value={answer}>
                        {answer}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter>
              <Button type="submit" disabled={
                isLoading || 
                !newQuestion.question_text.trim() || 
                !newQuestion.correct_answer.trim() ||
                answers.length === 0
              }>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingQuestion ? 'Update Question' : 'Add Question'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Assignment Dialog */}
      {assigningTest && (
        <TestAssignmentDialog
          isOpen={isAssignmentDialogOpen}
          onClose={() => {
            setIsAssignmentDialogOpen(false);
            setAssigningTest(null);
          }}
          test={assigningTest}
          cohorts={cohorts}
          onAssign={handleTestAssignment}
        />
      )}

      {/* Add Category Dialog */}
      <Dialog open={isAddCategoryDialogOpen} onOpenChange={setIsAddCategoryDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Custom Category</DialogTitle>
            <DialogDescription>
              Create a new category for organizing your tests
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="category-name">Category Name</Label>
              <Input
                id="category-name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Enter category name"
                onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
              />
            </div>
            
            {customCategories.length > 0 && (
              <div className="space-y-2">
                <Label>Existing Custom Categories</Label>
                <div className="flex flex-wrap gap-2">
                  {customCategories.map(category => (
                    <Badge key={category} variant="outline" className="capitalize">
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsAddCategoryDialogOpen(false);
                setNewCategoryName('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddCategory}
              disabled={!newCategoryName.trim() || getAllCategories().includes(newCategoryName.trim().toLowerCase())}
            >
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Test</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the test "{selectedTest?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            
            <Button
              variant="destructive"
              onClick={handleDeleteTest}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Test'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Test Dialog */}
      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Share Test</DialogTitle>
            <DialogDescription>
              Share "{sharingTest?.name}" with other teachers
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Select Teachers to Share With</Label>
              <div className="mt-2 space-y-2 max-h-60 overflow-y-auto border rounded-md p-3">
                {availableTeachers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Loading teachers...</p>
                ) : (
                  availableTeachers
                    .filter(teacher => teacher.id !== teacherId) // Exclude current user
                    .map((teacher) => (
                    <div key={teacher.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`teacher-${teacher.id}`}
                        checked={selectedTeachers.includes(teacher.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedTeachers([...selectedTeachers, teacher.id]);
                          } else {
                            setSelectedTeachers(selectedTeachers.filter(id => id !== teacher.id));
                          }
                        }}
                      />
                      <Label 
                        htmlFor={`teacher-${teacher.id}`} 
                        className="text-sm font-normal cursor-pointer"
                      >
                        {teacher.first_name} {teacher.last_name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {selectedTeachers.length > 0 && (
              <div className="text-sm text-muted-foreground">
                Selected {selectedTeachers.length} teacher{selectedTeachers.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsShareDialogOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleShareSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Sharing'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Statistics Dialog */}
      <TestStatsDialog
        isOpen={isStatsDialogOpen}
        onClose={() => {
          setIsStatsDialogOpen(false);
          setStatsTest(null);
          setTestStats(null);
        }}
        test={statsTest}
        stats={testStats}
      />

      {/* Delete Question Confirmation Dialog */}
      <Dialog open={isDeleteQuestionDialogOpen} onOpenChange={setIsDeleteQuestionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete Question</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this question? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {questionToDelete && (
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-2">Question to delete:</p>
              <div className="p-3 bg-gray-50 rounded border">
                <p className="text-sm font-medium">{questionToDelete.question_text}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteQuestionDialogOpen(false);
                setQuestionToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteQuestion}
              disabled={isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete Question'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast Notifications */}
      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`max-w-sm p-4 rounded-lg shadow-lg border ${
              toast.variant === 'destructive'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-green-50 border-green-200 text-green-800'
            } animate-in slide-in-from-right duration-300`}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {toast.variant === 'destructive' ? (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-sm">{toast.title}</h4>
                {toast.description && (
                  <p className="text-sm mt-1 opacity-90">{toast.description}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};

export default TestManager;